/**
 * Environment Hooks
 * React Query hooks for environment management.
 * @packageDocumentation
 */

import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useEffect, useCallback } from 'react';

import { trpc } from '@/api/tRpc';
import { GC_MEDIUM, STALE_MEDIUM } from '@/lib/query-options';
import { type Environment, useEnvironmentStore } from '@/stores';

export const ENVIRONMENT_QUERY_KEYS = {
  all: ['environments'] as const,
  retailers: () => [...ENVIRONMENT_QUERY_KEYS.all, 'retailers'] as const,
  retailer: (retailerCode: string) => [...ENVIRONMENT_QUERY_KEYS.retailers(), retailerCode] as const,
  environment: (retailerCode: string, environment: Environment) =>
    [...ENVIRONMENT_QUERY_KEYS.retailer(retailerCode), environment] as const,
};

/**
 * Hook for fetching and managing environments.
 *
 * @returns { object } Environment data and controls.
 */
export const useEnvironments = () => {
  const queryClient = useQueryClient();
  const {
    setError,
    setLoading,
    setRetailers,
    currentEnvSummary,
    currentEnvironment,
    currentRetailerCode,
    hasProductionAccess,
    hasEnvironmentAccess,
    setCurrentEnvironment,
    getRetailerEnvironments,
  } = useEnvironmentStore();

  const { data, error, refetch, isLoading } = trpc.retailers.list.useQuery(undefined, {
    gcTime: GC_MEDIUM,
    staleTime: STALE_MEDIUM,
    select: (result) => result.retailers,
  });

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  useEffect(() => {
    if (error) {
      setError(error instanceof Error ? error.message : 'Failed to load environments');
    }
  }, [error, setError]);

  useEffect(() => {
    if (data) {
      setRetailers(data);
    }
  }, [data, setRetailers]);

  const selectEnvironment = useCallback(
    (retailerCode: string, environment: Environment) => {
      setCurrentEnvironment(retailerCode, environment);
    },
    [setCurrentEnvironment],
  );

  const invalidateEnvironments = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ENVIRONMENT_QUERY_KEYS.all,
    });
  }, [queryClient]);

  // Memoize retailers to prevent creating new array reference on each render
  const retailers = useMemo(() => data ?? [], [data]);

  return {
    refetch,
    retailers,
    isLoading,
    currentEnvSummary,
    selectEnvironment,
    currentEnvironment,
    currentRetailerCode,
    hasProductionAccess,
    hasEnvironmentAccess,
    invalidateEnvironments,
    getRetailerEnvironments,
    error: error instanceof Error ? error.message : null,
  };
};
